// Parity P1 wave 1: the task's keyboard map (hybrid design §3.4: Esc · ⇧F full page · ] rail ·
// S status · A assignee · D due · P priority · C complete/reopen · M move · Alt+↑/↓ reorder).
// Keys act while focus is inside the task host and not in a text field (typing a title or a
// comment never triggers them). Menus open by clicking their trigger (`data-hotkey`), so the key
// and the click are one path. Esc is the drawer's own (Base UI) and the full page's back.
import { type RefObject, useEffect, useRef } from "react";

export const KEYBOARD_MAP: { keys: string; label: string }[] = [
  { keys: "Esc", label: "Close (the full page: back to where it was opened)" },
  { keys: "⇧F", label: "Open as a full page" },
  { keys: "]", label: "Show or fold the conversation rail" },
  { keys: "S", label: "Status" },
  { keys: "A", label: "Assignee" },
  { keys: "D", label: "Dates" },
  { keys: "P", label: "Priority" },
  { keys: "C", label: "Complete or reopen" },
  { keys: "M", label: "Move to board…" },
  { keys: "Alt ↑ / ↓", label: "Reorder in the layout editor" },
];

/** Whether the event comes from somewhere the user types. */
export function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type;
    return !["checkbox", "radio", "button", "submit"].includes(type);
  }
  return false;
}

export type TaskKeyHandlers = {
  fullPage?: () => void;
  rail: () => void;
  complete: () => void;
  /** Shows the Details tab (where the D, P and M menus are). */
  details?: () => void;
};

/** Binds the map to the host element (keydown bubbling from inside it). */
export function useTaskKeys(root: RefObject<HTMLElement | null>, handlers: TaskKeyHandlers) {
  const latest = useRef(handlers);
  latest.current = handlers;
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      const h = latest.current;
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      // A menu or popover that is open handles its own keys.
      if (document.querySelector("[data-slot=dropdown-menu-content], [role=menu]")) return;
      const find = (key: string) => el.querySelector<HTMLElement>(`[data-hotkey="${key}"]`);
      const click = (key: string) => {
        const t = find(key);
        // TASK-DRAWER-CHAT: a menu in a hidden Details tab (or folded rail): show it first.
        if (!t?.getClientRects().length && h.details) {
          h.details();
          requestAnimationFrame(() => requestAnimationFrame(() => find(key)?.click()));
          return true;
        }
        if (!t) return false;
        t.click();
        return true;
      };
      let done = false;
      if (e.key === "F" && e.shiftKey && h.fullPage) {
        h.fullPage();
        done = true;
      } else if (e.key === "]") {
        h.rail();
        done = true;
      } else if (!e.shiftKey && (e.key === "c" || e.key === "C")) {
        h.complete();
        done = true;
      } else if (!e.shiftKey && ["s", "a", "d", "p", "m"].includes(e.key.toLowerCase())) {
        done = click(e.key.toLowerCase());
      }
      if (done) e.preventDefault();
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [root]);
}
